import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { TouristPlace, TouristPlaceFormData, TouristPlaceTranslation } from '../../resources/types/touristPlacesTypes';
import {
  createTouristPlaceAsync,
  updateTouristPlaceAsync,
  uploadTouristPlacePhotoAsync,
} from '../../redux/slices/touristPlacesSlice';

interface TouristPlaceModalProps {
  open: boolean;
  onClose: () => void;
  place: TouristPlace | null;
  cityId: string;
}

const TouristPlaceModal: React.FC<TouristPlaceModalProps> = ({
  open,
  onClose,
  place,
  cityId,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<TouristPlaceFormData>({
    city: cityId,
    coordinates: [0, 0],
    name: '',
    description: { en: '', ua: '', de: '', es: '' },
    phone: '',
    website: '',
    workingHours: '',
    photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (place) {
        const desc = place.description;
        let descObj: TouristPlaceTranslation = { en: '', ua: '', de: '', es: '' };
        if (typeof desc === 'string') {
          descObj = { en: desc, ua: '', de: '', es: '' };
        } else if (desc && typeof desc === 'object') {
          descObj = { en: desc.en || '', ua: desc.ua || '', de: desc.de || '', es: desc.es || '' };
        }
        setFormData({
          city: place.city,
          coordinates: place.coordinates,
          name: place.name,
          description: descObj,
          phone: place.phone || '',
          website: place.website || '',
          workingHours: place.workingHours || '',
          photos: place.photos || [],
        });
      } else {
        setFormData({
          city: cityId,
          coordinates: [0, 0],
          name: '',
          description: { en: '', ua: '', de: '', es: '' },
          phone: '',
          website: '',
          workingHours: '',
          photos: [],
        });
      }
    }
  }, [open, place, cityId]);

  const handleChange = useCallback((field: keyof TouristPlaceFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  }, []);

  const handleCoordinateChange = useCallback((index: number) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(e.target.value) || 0;
    setFormData(prev => {
      const newCoords: [number, number] = [...prev.coordinates];
      newCoords[index] = value;
      return { ...prev, coordinates: newCoords };
    });
  }, []);

  const handlePhotoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const currentPhotos = formData.photos || [];
    if (currentPhotos.length >= 3) {
      alert('Максимум 3 фото');
      return;
    }

    setUploading(true);
    try {
      const file = files[0];
      const filename = await uploadTouristPlacePhotoAsync(file);
      if (filename) {
        setFormData(prev => ({
          ...prev,
          photos: [...(prev.photos || []), filename].slice(0, 3),
        }));
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [formData.photos]);

  const handleRemovePhoto = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: (prev.photos || []).filter((_, i) => i !== index),
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      alert('Введіть назву місця');
      return;
    }

    setSaving(true);
    const dataToSubmit: TouristPlaceFormData = {
      ...formData,
      city: cityId,
    };

    if (place?._id) {
      dispatch(updateTouristPlaceAsync(place._id, dataToSubmit, () => {
        setSaving(false);
        onClose();
      }));
    } else {
      dispatch(createTouristPlaceAsync(dataToSubmit, () => {
        setSaving(false);
        onClose();
      }));
    }
  }, [dispatch, formData, place, cityId, onClose]);

  const getPhotoUrl = (filename: string) => {
    if (!filename) return '';
    return `https://whee.pro/public/${filename}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {place ? 'Редагувати місце' : 'Нове туристичне місце'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Назва"
              value={formData.name}
              onChange={handleChange('name')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Опис (EN)"
              value={(formData.description as TouristPlaceTranslation)?.en || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                description: { ...(prev.description as TouristPlaceTranslation), en: e.target.value }
              }))}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Опис (UA)"
              value={(formData.description as TouristPlaceTranslation)?.ua || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                description: { ...(prev.description as TouristPlaceTranslation), ua: e.target.value }
              }))}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Опис (DE)"
              value={(formData.description as TouristPlaceTranslation)?.de || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                description: { ...(prev.description as TouristPlaceTranslation), de: e.target.value }
              }))}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Опис (ES)"
              value={(formData.description as TouristPlaceTranslation)?.es || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                description: { ...(prev.description as TouristPlaceTranslation), es: e.target.value }
              }))}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Довгота (lng)"
              type="number"
              value={formData.coordinates[0]}
              onChange={handleCoordinateChange(0)}
              inputProps={{ step: 'any' }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Широта (lat)"
              type="number"
              value={formData.coordinates[1]}
              onChange={handleCoordinateChange(1)}
              inputProps={{ step: 'any' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Телефон"
              value={formData.phone}
              onChange={handleChange('phone')}
              placeholder="+380441234567"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Вебсайт"
              value={formData.website}
              onChange={handleChange('website')}
              placeholder="https://example.com"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Години роботи"
              value={formData.workingHours}
              onChange={handleChange('workingHours')}
              placeholder="9:00–19:00"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Фото (до 3 шт.)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {(formData.photos || []).map((photo, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                  }}
                >
                  <img
                    src={getPhotoUrl(photo)}
                    alt={`Photo ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                    onError={(e: any) => {
                      e.target.src = 'https://via.placeholder.com/100?text=Error';
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePhoto(index)}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {(formData.photos?.length || 0) < 3 && (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    width: 100,
                    height: 100,
                    border: '2px dashed rgba(78, 205, 196, 0.5)',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#4ECDC4',
                      bgcolor: 'rgba(78, 205, 196, 0.05)',
                    },
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={24} sx={{ color: '#4ECDC4' }} />
                  ) : (
                    <>
                      <AddPhotoAlternateIcon sx={{ color: '#4ECDC4', mb: 0.5 }} />
                      <Typography variant="caption" color="textSecondary">
                        Додати
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || uploading}
          sx={{
            bgcolor: '#4ECDC4',
            '&:hover': { bgcolor: '#3BA99F' },
          }}
        >
          {saving ? <CircularProgress size={24} /> : place ? 'Зберегти' : 'Створити'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TouristPlaceModal;
